import { useState, useMemo, useCallback } from 'react';
import { MaterialData } from '../../../../../../types/materials';

interface CoatingFilters {
  status: string[];
  lotNumber: string[];
  manufacturer: string[];
  batchNumber: string[];
}

interface UseCoatingFiltersProps {
  materials: MaterialData[];
}

export const useCoatingFilters = ({ materials }: UseCoatingFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<CoatingFilters>({
    status: [],
    lotNumber: [],
    manufacturer: [],
    batchNumber: [],
  });

  // Extract unique lot numbers from materials
  const uniqueLotNumbers = useMemo(() => {
    const lotNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.coatingLotNumber) {
        lotNumbers.add(material.details.coatingLotNumber);
      }
    });
    return Array.from(lotNumbers).sort();
  }, [materials]);

  // Extract unique manufacturers from materials
  const uniqueManufacturers = useMemo(() => {
    const manufacturers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.coatingManufacturer) {
        manufacturers.add(material.details.coatingManufacturer);
      }
    });
    return Array.from(manufacturers).sort();
  }, [materials]);

  // Extract unique batch numbers from materials
  const uniqueBatchNumbers = useMemo(() => {
    const batchNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.coatingBatchNumber) {
        batchNumbers.add(material.details.coatingBatchNumber);
      }
    });
    return Array.from(batchNumbers).sort();
  }, [materials]);

  // Filter materials based on search query and filters
  const filteredMaterials = useMemo(() => {
    return materials.filter((material: MaterialData) => {
      // Search query filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        material.materialId?.toString().includes(searchLower) ||
        material.details?.coatingLotNumber?.toLowerCase().includes(searchLower) ||
        material.details?.coatingManufacturer?.toLowerCase().includes(searchLower) ||
        material.details?.coatingBatchNumber?.toLowerCase().includes(searchLower) ||
        material.assemblyNumbers?.some((assembly: string) => assembly?.toLowerCase().includes(searchLower)) ||
        (material.materialStatus)?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = filters.status.length === 0 || 
        filters.status.includes(material.materialStatus || '');

      // Lot number filter
      const matchesLotNumber = filters.lotNumber.length === 0 || 
        (material.details?.coatingLotNumber && filters.lotNumber.includes(material.details.coatingLotNumber));

      // Manufacturer filter
      const matchesManufacturer = filters.manufacturer.length === 0 || 
        (material.details?.coatingManufacturer && filters.manufacturer.includes(material.details.coatingManufacturer));

      // Batch number filter
      const matchesBatchNumber = filters.batchNumber.length === 0 || 
        (material.details?.coatingBatchNumber && filters.batchNumber.includes(material.details.coatingBatchNumber));

      return matchesSearch && matchesStatus && matchesLotNumber && 
             matchesManufacturer && matchesBatchNumber;
    });
  }, [materials, searchQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || 
           filters.status.length > 0 || 
           filters.lotNumber.length > 0 ||
           filters.manufacturer.length > 0 ||
           filters.batchNumber.length > 0;
  }, [searchQuery, filters]);

  // Handle filter changes
  const onFilterChange = useCallback((key: keyof CoatingFilters) => (value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters
  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      status: [],
      lotNumber: [],
      manufacturer: [],
      batchNumber: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueLotNumbers,
    uniqueManufacturers,
    uniqueBatchNumbers,
    filteredMaterials,
    hasActiveFilters,
    filteredCount: filteredMaterials.length,
    onResetFilters,
  };
}; 